import React, { useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { WIDGET_STATE } from "./kinds";
import { describeSubmission } from "./model/submission";
import { useWidgetMsg } from "../../../hooks/widget/useWidgetMsg";
import { reportWidgetEvent } from "../../../utils/widgetTelemetry";

/**
 * Never a blank (D-W10, §B5): `fallbackText` as ordinary reply text, reported ONCE per record as
 * `widget_fallback_v1{tier, kind, reason}`.
 */
export function WidgetFallback({ envelope, reason, recordId }) {
	const tier = String(envelope?.tier ?? "");
	const kind = String(envelope?.kind ?? "");
	useEffect(() => {
		reportWidgetEvent("widget_fallback_v1", { tier, kind, reason }, { once: true, onceKey: `${recordId}|${reason}` });
	}, [tier, kind, reason, recordId]);
	const text = typeof envelope?.fallbackText === "string" ? envelope.fallbackText : "";
	if (!text) return null;
	return (
		<Typography className="widget-fallback" data-fallback-reason={reason} sx={{ fontSize: 14, lineHeight: 1.6, whiteSpace: "pre-wrap", color: "var(--w-text)" }}>
			{text}
		</Typography>
	);
}

const timeOf = (iso, lang) => {
	const at = Date.parse(iso);
	return Number.isFinite(at) ? new Intl.DateTimeFormat(lang, { hour: "2-digit", minute: "2-digit" }).format(new Date(at)) : null;
};

/**
 * A submitted widget collapses to what was sent (AC-11; canvas "Sent: …"; a confirmation reads
 * Confirmed / Declined); an expired one says it is no longer available. Not interactive.
 */
export function WidgetCollapsed({ envelope, state, values }) {
	const { msg, lang } = useWidgetMsg();
	let text = msg("widget.expired", "This is no longer available.");
	let time = null;
	if (state === WIDGET_STATE.SUBMITTED) {
		const line = describeSubmission(envelope, values, {
			confirmed: msg("widget.confirmed", "Confirmed"),
			declined: msg("widget.declined", "Declined"),
			none: msg("widget.none", "None of these work"),
			lang,
		});
		const sent = msg("widget.sent", "Sent");
		text = envelope.kind === "confirmation" && line ? line : line ? `${sent}: ${line}` : sent;
		const at = timeOf(envelope.submittedAt, lang);
		time = at ? msg("widget.submitted_at", "Sent at {time}", { time: at }) : null;
	}
	return (
		<Box
			className="widget-native widget-collapsed"
			data-widget-kind={envelope.kind}
			data-widget-state={state}
			sx={{ width: "100%", boxSizing: "border-box", border: "1px solid var(--w-border)", borderRadius: "11px", px: 1.5, py: 1, color: "var(--w-text)" }}
		>
			<Typography sx={{ fontSize: 12.5, overflowWrap: "anywhere" }}>{text}</Typography>
			{time && <Typography sx={{ fontSize: 11 }}>{time}</Typography>}
		</Box>
	);
}

/** A render error inside a widget falls back to its text (`reason: render_error`) instead of a blank. */
export class WidgetErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { failed: false };
	}

	static getDerivedStateFromError() {
		return { failed: true };
	}

	componentDidCatch(error) {
		console.error("@@ chat-widget @@ widget render failed", { recordId: this.props.recordId, kind: this.props.envelope?.kind, error: error?.message });
	}

	render() {
		if (this.state.failed) return <WidgetFallback envelope={this.props.envelope} reason="render_error" recordId={this.props.recordId} />;
		return this.props.children;
	}
}
